import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { responseStatus } from "utils/types";
import { request } from "utils/request";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import {
  ActivityCacheOwner,
  getActivityCompanyKey,
  getActivityCompanyQuery,
  initialActivityCacheOwner,
  shouldFetchActivityData,
  startActivityRequest,
} from "utils/activityTableCache";
import {
  AllWeeklyCheckInTableData,
  CheckInResponseSummaryResponse,
  CoachBoWeeklyCheckInActivityReports,
  WeeklyCheckInLineChartState,
  WeeklyCheckInTableRowData,
  WeeklyCheckInTableRowDataBase,
  WeeklyCheckInRegradeData,
  WeeklyCheckInLineChartResponse,
  WeeklyCheckInUserStatuses,
  WeeklyCheckInCompanySettings,
  WeeklyCheckInCustomList,
  WeeklyCheckInActivityEmailReportTableData,
  WeeklyCheckInActivityEmailReport,
  WeeklyCheckInActivityEmailReportState,
  WeeklyCheckinEmailReportDataByTimeAndType,
  SampleWeeklyCheckInData,
  SampleWeeklyCheckInEmailReportModalState,
  CheckInEmailReportType,
  CelebrateAndSupports,
  GetCelebrateAndSupportUsersPayload,
  UserWeeklyCheckInTableRowData,
  ICSnapshotData,
  AiServiceICSnapshotData,
  ExternalReportRecipient,
  WeeklyCheckInCadencePayload,
  WeeklyCheckInCadence,
  WeeklyCheckInCadenceSchedule,
} from "./types";
import {
  aiServiceFetch,
  AiServiceError,
  isUsingAIService,
} from "utils/aiServiceRequest";
import {
  getEntitiesToUpdateForCustomList,
  clearAlertFromStore,
  getUpdateWeeklyCheckInCompanySettingsToastMessage,
  getWeeklyCheckInBreakDownKey,
  sortWeeklyCheckInTableRowData,
} from "./helpers";
import { updateRegradedCheckInInAllIntervals } from "./tableDataUpdaters";
import { toast } from "react-toastify";
import { successNotificationOptions } from "utils/constants";
import { getPeopleOrPerson, getS } from "utils/helperFunctions";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "./constants";
import {
  getUserNotificationsAndEmailSettings,
  getUserSubordinates,
} from "../UserSettings/slice";
import { updateTeamsBasedOnWeeklyCheckInCustomList } from "../Global/slice";
import { updateDepartmentsBasedOnWeeklyCheckInCustomList } from "../AdminConsole/slice";
import { getQueryParamsFromObject } from "../Global/helpers";

// ------------------ State Type/Structure ------------------
export interface WeeklyCheckInMonitorState {
  coachBoWeeklyCheckInActivityReports: CoachBoWeeklyCheckInActivityReports;
  sampleWeeklyCheckInActivityData: SampleWeeklyCheckInData | null;
  subordinatesWeeklyCheckInActivityReports: CoachBoWeeklyCheckInActivityReports;
  sampleSubordinatesWeeklyCheckInActivityReports: CoachBoWeeklyCheckInActivityReports;
  savedCheckInResponseSummaries: Record<string, CheckInResponseSummaryResponse>;
  allWeeklyCheckInTableData: AllWeeklyCheckInTableData;
  weeklyCheckInTableDataOwner: ActivityCacheOwner;
  SubordinatesWeeklyCheckinTableData: AllWeeklyCheckInTableData;
  weeklyCheckInLineChartData: WeeklyCheckInLineChartState;
  SubordinatesWeeklyCheckInLineChartData: WeeklyCheckInLineChartState;
  companyWeeklyCheckInCustomList: WeeklyCheckInCustomList | null;
  usersSubordinatesIds: number[];
  weeklyCheckInUserStatuses: WeeklyCheckInUserStatuses | null;
  weeklyCheckInCompanySettings: WeeklyCheckInCompanySettings | null;
  weeklyCheckInCadenceSchedule: WeeklyCheckInCadenceSchedule | null;
  getWeeklyCheckInCadenceScheduleStatus: responseStatus;
  weeklyCheckInBreakdownTableData: {
    [payloadKey: string]: WeeklyCheckInTableRowData[];
  };
  showWeeklyCheckInActivityEmptyState: boolean;
  executiveWeeklyCheckInCompanyCustomList: number[];
  showWeeklyCheckInSampleData: boolean;
  celebrateAndSupports: {
    [memoizedKey: string]: CelebrateAndSupports;
  };
  individualCheckInActivitySnapshot:
    | ICSnapshotData
    | AiServiceICSnapshotData
    | null;
  updatingCompanyExecutiveWeeklyCheckInCustomListStatus: responseStatus;
  getExecutiveWeeklyCheckInCompanyCustomListStatus: responseStatus;
  externalExecutiveReportRecipients: ExternalReportRecipient[];
  getExternalExecutiveReportRecipientsStatus: responseStatus;
  updatingExternalExecutiveReportRecipientsStatus: responseStatus;
  updateWeeklyCheckInCompanySettingsStatus: responseStatus;
  getWeeklyCheckInCompanySettingsStatus: responseStatus;
  getCompanyWeeklyCheckInCustomListStatus: responseStatus;
  updateCompanyWeeklyCheckInCustomListStatus: responseStatus;
  weeklyCheckInActivityEmailReportsTableData: WeeklyCheckinEmailReportDataByTimeAndType;
  weeklyCheckInActivityEmailReportsTableDataOwner: ActivityCacheOwner;
  subordinatesWeeklyCheckInActivityEmailReportsTableData: WeeklyCheckinEmailReportDataByTimeAndType;
  weeklyCheckInActivityEmailReports: WeeklyCheckInActivityEmailReportState;
  sendSampleWeeklyCheckInEmailStatus: responseStatus;
  getWeeklyCheckInCelebrateAndSupportStatus: responseStatus;
  getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus: responseStatus;
  getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus: responseStatus;
  getCoachBoWeeklyCheckInActivityReportStatus: responseStatus;
  getSampleWeeklyCheckInActivityDataStatus: responseStatus;
  getSampleSubordinatesWeeklyCheckInActivityReportStatus: responseStatus;
  getSubordinatesWeeklyCheckInActivityReportStatus: responseStatus;
  getWeeklyCheckInModuleStatus: responseStatus;
  regradeWeeklyCheckInStatusByThread: {
    [threadId: string]: responseStatus;
  };
  getCheckInResponseSummaryStatus: responseStatus;
  getWeeklyCheckInTableDataStatus: responseStatus;
  getSubordinatesWeeklyCheckinTableDataStatus: responseStatus;
  getSubordinatesWeeklyCheckInLineChartDataStatus: responseStatus;
  getWeeklyCheckInLineChartDataStatus: responseStatus;
  getSpanOfControlStatus: responseStatus;
  getWeeklyCheckInUserStatusesStatus: responseStatus;
  sendWeeklyCheckInRemindersStatus: responseStatus;
  sendWeeklyCheckInRemindersPerUserAccountIdStatus: {
    [userAccountId: number]: responseStatus;
  };
  getWeeklyCheckInActivityEmailReportsTableDataStatus: responseStatus;
  getWeeklyCheckInActivityEmailReportByIdStatus: {
    [checkInEmailReportId: number]: responseStatus;
  };
  sampleWeeklyCheckInEmailReportModalState: SampleWeeklyCheckInEmailReportModalState;
  getWeeklyCheckInBreakdownTableDataStatus: responseStatus;
  getIndividualCheckInActivitySnapshotStatus: responseStatus;
  acknowledgeHrConcernAlertStatus: responseStatus;
  harmDetectionAlertRecipients: number[];
  getHarmDetectionAlertRecipientsStatus: responseStatus;
  updateHarmDetectionAlertRecipientsStatus: responseStatus;
}

// ------------------ InitialState ------------------
const initialState: WeeklyCheckInMonitorState = {
  coachBoWeeklyCheckInActivityReports: {},
  sampleWeeklyCheckInActivityData: null,
  sampleSubordinatesWeeklyCheckInActivityReports: {},
  savedCheckInResponseSummaries: {},
  allWeeklyCheckInTableData: {},
  weeklyCheckInTableDataOwner: initialActivityCacheOwner,
  weeklyCheckInLineChartData: {},
  SubordinatesWeeklyCheckInLineChartData: {},
  subordinatesWeeklyCheckInActivityReports: {},
  SubordinatesWeeklyCheckinTableData: {},
  usersSubordinatesIds: [],
  weeklyCheckInCompanySettings: null,
  weeklyCheckInCadenceSchedule: null,
  getWeeklyCheckInCadenceScheduleStatus: "idle",
  weeklyCheckInUserStatuses: null,
  weeklyCheckInActivityEmailReportsTableData: {},
  weeklyCheckInActivityEmailReportsTableDataOwner: initialActivityCacheOwner,
  weeklyCheckInActivityEmailReports: {},
  subordinatesWeeklyCheckInActivityEmailReportsTableData: {},
  showWeeklyCheckInActivityEmptyState: false,
  executiveWeeklyCheckInCompanyCustomList: [],
  companyWeeklyCheckInCustomList: null,
  showWeeklyCheckInSampleData: false,
  celebrateAndSupports: {},
  weeklyCheckInBreakdownTableData: {},
  sampleWeeklyCheckInEmailReportModalState: {},
  sendWeeklyCheckInRemindersPerUserAccountIdStatus: {},
  getWeeklyCheckInActivityEmailReportByIdStatus: {},
  regradeWeeklyCheckInStatusByThread: {},
  individualCheckInActivitySnapshot: null,
  getWeeklyCheckInCelebrateAndSupportStatus: "idle",
  getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus: "idle",
  updatingCompanyExecutiveWeeklyCheckInCustomListStatus: "idle",
  getExecutiveWeeklyCheckInCompanyCustomListStatus: "idle",
  externalExecutiveReportRecipients: [],
  getExternalExecutiveReportRecipientsStatus: "idle",
  updatingExternalExecutiveReportRecipientsStatus: "idle",
  updateWeeklyCheckInCompanySettingsStatus: "idle",
  getWeeklyCheckInCompanySettingsStatus: "idle",
  getCompanyWeeklyCheckInCustomListStatus: "idle",
  updateCompanyWeeklyCheckInCustomListStatus: "idle",
  getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus: "idle",
  getSubordinatesWeeklyCheckinTableDataStatus: "idle",
  getSubordinatesWeeklyCheckInActivityReportStatus: "idle",
  getSubordinatesWeeklyCheckInLineChartDataStatus: "idle",
  getCoachBoWeeklyCheckInActivityReportStatus: "idle",
  getWeeklyCheckInModuleStatus: "idle",
  getCheckInResponseSummaryStatus: "idle",
  getWeeklyCheckInTableDataStatus: "idle",
  getWeeklyCheckInLineChartDataStatus: "idle",
  getSpanOfControlStatus: "idle",
  getWeeklyCheckInUserStatusesStatus: "idle",
  sendWeeklyCheckInRemindersStatus: "idle",
  sendSampleWeeklyCheckInEmailStatus: "idle",
  getWeeklyCheckInActivityEmailReportsTableDataStatus: "idle",
  getSampleWeeklyCheckInActivityDataStatus: "idle",
  getSampleSubordinatesWeeklyCheckInActivityReportStatus: "idle",
  getWeeklyCheckInBreakdownTableDataStatus: "idle",
  getIndividualCheckInActivitySnapshotStatus: "idle",
  acknowledgeHrConcernAlertStatus: "idle",
  harmDetectionAlertRecipients: [],
  getHarmDetectionAlertRecipientsStatus: "idle",
  updateHarmDetectionAlertRecipientsStatus: "idle",
};

// ------------------ Asynchronous API calls ------------------
// ------------------------------------ GET -------------------------------------
export const getCoachBoWeeklyCheckInActivityReport = createAsyncThunk(
  "weeklyCheckInMonitor/getCoachBoWeeklyCheckInActivityReport",
  async () => {
    if (isUsingAIService()) {
      return await aiServiceFetch<CoachBoWeeklyCheckInActivityReports>(
        "/v1/checkInActivity",
        { method: "GET" }
      );
    }
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/checkInActivity`;
    const response = (await request(
      requestUrl
    )) as CoachBoWeeklyCheckInActivityReports;
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getCoachBoWeeklyCheckInActivityReportStatus },
      } = getState() as RootState;
      if (getCoachBoWeeklyCheckInActivityReportStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getSampleWeeklyCheckInActivityData = createAsyncThunk(
  "weeklyCheckInMonitor/getSampleWeeklyCheckInActivityData",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInSampleData`;
    const response = (await request(requestUrl)) as SampleWeeklyCheckInData;
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getSampleWeeklyCheckInActivityDataStatus },
      } = getState() as RootState;
      if (getSampleWeeklyCheckInActivityDataStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getSubordinatesWeeklyCheckInActivityReport = createAsyncThunk(
  "weeklyCheckInMonitor/getSubordinatesWeeklyCheckInActivityReport",
  async () => {
    if (isUsingAIService()) {
      return await aiServiceFetch<CoachBoWeeklyCheckInActivityReports>(
        "/v1/checkInActivity?reportsOnly=true",
        { method: "GET" }
      );
    }
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/checkInActivity?reportsOnly=true`;
    const response = (await request(
      requestUrl
    )) as CoachBoWeeklyCheckInActivityReports;
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: {
          getSubordinatesWeeklyCheckInActivityReportStatus,
        },
      } = getState() as RootState;
      if (getSubordinatesWeeklyCheckInActivityReportStatus !== "idle") {
        return false;
      }
    },
  }
);

// This endpoint will determine if we show the empty state for the check-in activity report
export const getWeeklyCheckInModuleStatus = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInModuleStatus",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInStatus`;
    const response = (await request(requestUrl)) as {
      atLeastOneTeamHasCheckInsEnabled: boolean;
    };
    return response?.atLeastOneTeamHasCheckInsEnabled;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getWeeklyCheckInModuleStatus },
      } = getState() as RootState;
      if (getWeeklyCheckInModuleStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getWeeklyCheckInTableData = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInTableData",
  async (
    {
      timeInterval,
      entityType,
      companyAccountId,
    }: {
      timeInterval: string;
      entityType: string;
      companyAccountId?: string;
    },
    { getState }
  ) => {
    const companyKey = getActivityCompanyKey(getState() as RootState, companyAccountId);
    const companyParam = getActivityCompanyQuery(companyKey);
    let requestUrl;
    if (entityType === "user") {
      requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInUserTableData/${timeInterval}${companyParam}`;
    } else {
      requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInGroupTableData/${timeInterval}/${entityType}${companyParam}`;
    }

    let response = (await request(requestUrl)) as WeeklyCheckInTableRowData[];
    response = sortWeeklyCheckInTableRowData(response);

    return { response, timeInterval, entityType, companyKey };
  },
  {
    condition: ({ timeInterval, entityType, companyAccountId }, { getState }) => {
      const state = getState() as RootState;
      const {
        getWeeklyCheckInTableDataStatus,
        allWeeklyCheckInTableData,
        weeklyCheckInTableDataOwner,
      } = state.weeklyCheckInMonitor;
      return shouldFetchActivityData(
        weeklyCheckInTableDataOwner,
        getActivityCompanyKey(state, companyAccountId),
        getWeeklyCheckInTableDataStatus,
        !!allWeeklyCheckInTableData[timeInterval]?.[entityType]?.length
      );
    },
    getPendingMeta: ({ arg }, { getState }) => ({
      companyKey: getActivityCompanyKey(getState() as RootState, arg.companyAccountId),
    }),
  }
);

export const getSubordinatesWeeklyCheckinTableData = createAsyncThunk(
  "weeklyCheckInMonitor/getSubordinatesWeeklyCheckinTableData",
  async ({
    timeInterval,
    entityType,
  }: {
    timeInterval: string;
    entityType: string;
  }) => {
    // Select the appropriate endpoint based on the entity type
    // The reportsOnly parameter will need to be handled differently with new endpoints
    let requestUrl;
    if (entityType === "user") {
      requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInUserTableData/${timeInterval}?reportsOnly=true`;
    } else {
      // For 'team' or 'department' entity types
      requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInGroupTableData/${timeInterval}/${entityType}?reportsOnly=true`;
    }

    let response = (await request(requestUrl)) as WeeklyCheckInTableRowData[];
    response = sortWeeklyCheckInTableRowData(response);

    return { response, timeInterval, entityType };
  },
  {
    condition: ({ timeInterval, entityType }, { getState }) => {
      const {
        weeklyCheckInMonitor: {
          getSubordinatesWeeklyCheckinTableDataStatus,
          SubordinatesWeeklyCheckinTableData,
        },
      } = getState() as RootState;
      if (
        getSubordinatesWeeklyCheckinTableDataStatus === "loading" ||
        SubordinatesWeeklyCheckinTableData[timeInterval]?.[entityType]?.length
      ) {
        return false;
      }
    },
  }
);

export const getUserCheckInsFromEntityAndTimeInterval = createAsyncThunk(
  "weeklyCheckInMonitor/getUserCheckInsFromEntityAndTimeInterval",
  async (payload: {
    entityId: number;
    entityType: string;
    weeklyCheckInInstanceId: number;
  }) => {
    const { entityId, entityType, weeklyCheckInInstanceId } = payload;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/userBreakDown/${weeklyCheckInInstanceId}/${entityType}/${entityId}`;
    const response = (await request(
      requestUrl
    )) as UserWeeklyCheckInTableRowData[];
    return {
      response,
      payloadKey: getWeeklyCheckInBreakDownKey(payload),
    };
  },
  {
    condition: (payload, { getState }) => {
      const payloadKey = getWeeklyCheckInBreakDownKey(payload);
      const {
        weeklyCheckInMonitor: { weeklyCheckInBreakdownTableData },
      } = getState() as RootState;
      return !weeklyCheckInBreakdownTableData[payloadKey];
    },
  }
);

export const getWeeklyCheckInLineChartData = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInLineChartData",
  async ({
    timeInterval,
    index = 1,
  }: {
    timeInterval: string;
    index?: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInLineChartData/${timeInterval}/${index}`;
    const response = (await request(
      requestUrl
    )) as WeeklyCheckInLineChartResponse;

    if (!response || Object.keys(response).length === 0) {
      return { response: null, timeInterval, index };
    }
    // If the initial index is 1 that means its the most recent data
    // so hasNextTimePeriodData should be false, otherwise it should be true
    if (index !== 1) {
      response.hasNextTimePeriodData = true;
    }

    return { response, timeInterval, index };
  },
  {
    condition: ({ timeInterval, index = 1 }, { getState }) => {
      const {
        weeklyCheckInMonitor: { weeklyCheckInLineChartData },
      } = getState() as RootState;
      if (weeklyCheckInLineChartData?.[timeInterval]?.[index]) {
        // If the data is already fetched, we don't need to fetch it again
        return false;
      }
    },
  }
);

export const getSubordinatesWeeklyCheckInLineChartData = createAsyncThunk(
  "weeklyCheckInMonitor/getSubordinatesWeeklyCheckInLineChartData",
  async ({
    timeInterval,
    index = 1,
  }: {
    timeInterval: string;
    index?: number;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInLineChartData/${timeInterval}/${index}?reportsOnly=true`;
    const response = (await request(
      requestUrl
    )) as WeeklyCheckInLineChartResponse;

    if (!response || Object.keys(response).length === 0) {
      return { response: null, timeInterval, index };
    }
    // If the initial index is 1 that means its the most recent data
    // so hasNextTimePeriodData should be false, otherwise it should be true
    if (index !== 1) {
      response.hasNextTimePeriodData = true;
    }

    return { response, timeInterval, index };
  },
  {
    condition: ({ timeInterval, index = 1 }, { getState }) => {
      const {
        weeklyCheckInMonitor: { SubordinatesWeeklyCheckInLineChartData },
      } = getState() as RootState;
      if (SubordinatesWeeklyCheckInLineChartData?.[timeInterval]?.[index]) {
        // If the data is already fetched, we don't need to fetch it again
        return false;
      }
    },
  }
);

export const getCheckInResponseSummary = createAsyncThunk(
  "weeklyCheckInMonitor/getCheckInResponseSummary",
  async (threadId: string) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckIn/${threadId}`;
    const response = (await request(
      requestUrl
    )) as CheckInResponseSummaryResponse;

    return { response, threadId };
  },
  {
    condition: (threadId, { getState }) => {
      const {
        weeklyCheckInMonitor: { savedCheckInResponseSummaries },
      } = getState() as RootState;
      if (savedCheckInResponseSummaries[threadId]) {
        return false;
      }
    },
  }
);

export const getUserSpanOfControl = createAsyncThunk(
  "weeklyCheckInMonitor/getUserSpanOfControl",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/spanOfControl`;
    const response = (await request(requestUrl)) as {
      spanOfControl: number[];
    };
    return response?.spanOfControl;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getSpanOfControlStatus },
      } = getState() as RootState;
      if (getSpanOfControlStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getWeeklyCheckInUserStatuses = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInUserStatuses",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInUserStatuses`;
    const response = (await request(requestUrl)) as WeeklyCheckInUserStatuses;
    return response;
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getWeeklyCheckInUserStatusesStatus },
      } = getState() as RootState;
      if (getWeeklyCheckInUserStatusesStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getWeeklyCheckInCompanySettings = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInCompanySettings",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInsCompanySettings`;
    const response = (await request(
      requestUrl
    )) as WeeklyCheckInCompanySettings;

    return response;
  },
  {
    condition: (
      payload: { forceRefresh?: boolean } | undefined,
      { getState }
    ) => {
      const forceRefresh = payload?.forceRefresh ?? false;
      const {
        weeklyCheckInMonitor: { getWeeklyCheckInCompanySettingsStatus },
      } = getState() as RootState;
      if (getWeeklyCheckInCompanySettingsStatus !== "idle" && !forceRefresh) {
        return false;
      }
    },
  }
);

export const getCompanyWeeklyCheckInCustomList = createAsyncThunk(
  "adminConsole/getCompanyWeeklyCheckInCustomList",
  async (
    payload:
      | { forceRefresh?: boolean; fetchManagerSubordinates?: boolean }
      | undefined,
    { dispatch }
  ) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/entitiesWithWeeklyCheckInsEnabled`;
    const response = (await request(requestUrl)) as WeeklyCheckInCustomList;

    if (payload?.fetchManagerSubordinates) {
      const userAccountIds = response?.managers?.map((m) => m.userAccountId);
      dispatch(getUserSubordinates(userAccountIds));
    }

    return response;
  },
  {
    condition: (payload, { getState }) => {
      const {
        weeklyCheckInMonitor: { getCompanyWeeklyCheckInCustomListStatus },
      } = getState() as RootState;
      if (
        getCompanyWeeklyCheckInCustomListStatus !== "idle" &&
        !payload?.forceRefresh
      ) {
        return false;
      }
    },
  }
);

export const getExecutiveWeeklyCheckInCompanyCustomList = createAsyncThunk(
  "adminConsole/getExecutiveWeeklyCheckInCompanyCustomList",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/usersWithExecutiveReportEnabled`;
    const response = (await request(requestUrl)) as number[];
    return response ?? [];
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: {
          getExecutiveWeeklyCheckInCompanyCustomListStatus,
        },
      } = getState() as RootState;
      if (getExecutiveWeeklyCheckInCompanyCustomListStatus !== "idle") {
        return false;
      }
    },
  }
);

export const getExternalExecutiveReportRecipients = createAsyncThunk(
  "weeklyCheckInMonitor/getExternalExecutiveReportRecipients",
  async () => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/externalExecutiveReportRecipients`;
    const response = (await request(requestUrl)) as {
      recipients: ExternalReportRecipient[];
    };
    return response?.recipients ?? [];
  },
  {
    condition: (_, { getState }) => {
      const {
        weeklyCheckInMonitor: { getExternalExecutiveReportRecipientsStatus },
      } = getState() as RootState;
      if (getExternalExecutiveReportRecipientsStatus === "loading") {
        return false;
      }
    },
  }
);

export const getWeeklyCheckInActivityEmailReportsTableData = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInActivityEmailReportsTableData",
  async (
    {
      timeInterval,
      reportType,
      companyAccountId,
    }: {
      timeInterval: string;
      reportType: string;
      companyAccountId?: string;
    },
    { getState }
  ) => {
    const companyKey = getActivityCompanyKey(getState() as RootState, companyAccountId);
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInActivityEmailReportsTableData/${timeInterval}/${reportType}${getActivityCompanyQuery(companyKey)}`;
    const response = (await request(
      requestUrl
    )) as WeeklyCheckInActivityEmailReportTableData[];

    return { response, timeInterval, reportType, companyKey };
  },
  {
    condition: ({ timeInterval, reportType, companyAccountId }, { getState }) => {
      const state = getState() as RootState;
      const {
        getWeeklyCheckInActivityEmailReportsTableDataStatus,
        weeklyCheckInActivityEmailReportsTableData,
        weeklyCheckInActivityEmailReportsTableDataOwner,
      } = state.weeklyCheckInMonitor;
      return shouldFetchActivityData(
        weeklyCheckInActivityEmailReportsTableDataOwner,
        getActivityCompanyKey(state, companyAccountId),
        getWeeklyCheckInActivityEmailReportsTableDataStatus,
        !!weeklyCheckInActivityEmailReportsTableData[timeInterval]?.[reportType]
          ?.length
      );
    },
    getPendingMeta: ({ arg }, { getState }) => ({
      companyKey: getActivityCompanyKey(getState() as RootState, arg.companyAccountId),
    }),
  }
);

export const getWeeklyCheckInCelebrateAndSupport = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInCelebrateAndSupport",
  async (payload: GetCelebrateAndSupportUsersPayload, { dispatch }) => {
    const memoizedKey = getQueryParamsFromObject({ ...payload });
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/celebrateAndSupportUsers?${memoizedKey}`;
    const response = (await request(requestUrl)) as CelebrateAndSupports;
    dispatch(
      saveWeeklyCheckInCelebrateAndSupport({
        response,
        memoizedKey,
      })
    );
    return { response, payload };
  },
  {
    condition: (payload, { getState }) => {
      const {
        weeklyCheckInMonitor: {
          getWeeklyCheckInCelebrateAndSupportStatus,
          celebrateAndSupports,
        },
      } = getState() as RootState;
      const memoizedKey = getQueryParamsFromObject({
        ...payload,
      });

      if (
        getWeeklyCheckInCelebrateAndSupportStatus === "loading" ||
        celebrateAndSupports[memoizedKey]
      ) {
        return false;
      }
    },
  }
);

export const getWeeklyCheckInCelebrateAndSupportForSubordinates =
  createAsyncThunk(
    "weeklyCheckInMonitor/getWeeklyCheckInCelebrateAndSupportForSubordinates",
    async (payload: GetCelebrateAndSupportUsersPayload, { dispatch }) => {
      const memoizedKey = getQueryParamsFromObject({
        ...payload,
        reportsOnly: true,
      });
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/celebrateAndSupportUsers?${memoizedKey}`;
      const response = (await request(requestUrl)) as CelebrateAndSupports;
      dispatch(
        saveWeeklyCheckInCelebrateAndSupport({
          response,
          memoizedKey,
        })
      );

      return response;
    },
    {
      condition: (payload, { getState }) => {
        const {
          weeklyCheckInMonitor: {
            getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus,
            celebrateAndSupports,
          },
        } = getState() as RootState;
        const memoizedKey = getQueryParamsFromObject({
          ...payload,
          reportsOnly: true,
        });
        if (
          getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus ===
            "loading" ||
          celebrateAndSupports[memoizedKey]
        ) {
          return false;
        }
      },
    }
  );

export const getSubordinatesWeeklyCheckInActivityEmailReportsTableData =
  createAsyncThunk(
    "weeklyCheckInMonitor/getSubordinatesWeeklyCheckInActivityEmailReportsTableData",
    async ({
      timeInterval,
      reportType,
    }: {
      timeInterval: string;
      reportType: string;
    }) => {
      const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInActivityEmailReportsTableData/${timeInterval}/${reportType}?reportsOnly=true`;
      const response = (await request(
        requestUrl
      )) as WeeklyCheckInActivityEmailReportTableData[];

      return { response, timeInterval, reportType };
    },
    {
      condition: ({ timeInterval, reportType }, { getState }) => {
        const {
          weeklyCheckInMonitor: {
            getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus,
            subordinatesWeeklyCheckInActivityEmailReportsTableData,
          },
        } = getState() as RootState;
        if (
          getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus ===
            "loading" ||
          subordinatesWeeklyCheckInActivityEmailReportsTableData[
            timeInterval
          ]?.[reportType]?.length
        ) {
          return false;
        }
      },
    }
  );

export const getWeeklyCheckInActivityEmailReportById = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInActivityEmailReportById",
  async (checkInEmailReportId: number, { dispatch }) => {
    dispatch(
      setGetWeeklyCheckInActivityEmailReportByIdStatus({
        checkInEmailReportId,
        status: "loading",
      })
    );

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInReport/${checkInEmailReportId}`;
    let response = (await request(
      requestUrl,
      { method: "GET" },
      true
    )) as WeeklyCheckInActivityEmailReport & { status: number };

    if (response.status !== 200) {
      dispatch(
        setGetWeeklyCheckInActivityEmailReportByIdStatus({
          checkInEmailReportId,
          status: "failed",
        })
      );
      throw new Error("Failed to fetch weekly check-in activity email report");
    }
    // remove status from response
    const { status, ...parsedResponse } = response;

    dispatch(
      setGetWeeklyCheckInActivityEmailReportByIdStatus({
        checkInEmailReportId,
        status: "succeeded",
      })
    );
    return { response: parsedResponse, checkInEmailReportId };
  },
  {
    condition: (checkInEmailReportId, { getState }) => {
      const {
        weeklyCheckInMonitor: { weeklyCheckInActivityEmailReports },
      } = getState() as RootState;
      if (weeklyCheckInActivityEmailReports[checkInEmailReportId]) {
        return false;
      }
    },
  }
);

export const getIndividualCheckInActivitySnapshot = createAsyncThunk(
  "weeklyCheckInMonitor/getIndividualCheckInActivitySnapshot",
  async () => {
    if (isUsingAIService()) {
      return await aiServiceFetch<AiServiceICSnapshotData>(
        "/v1/individualCheckInActivitySnapshot",
        { method: "GET" }
      );
    }
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/individualCheckInActivitySnapshot`;
    const response = (await request(requestUrl)) as ICSnapshotData;

    return response;
  },
  {
    condition: (_userId, { getState }) => {
      const {
        weeklyCheckInMonitor: { getIndividualCheckInActivitySnapshotStatus },
      } = getState() as RootState;
      if (getIndividualCheckInActivitySnapshotStatus !== "idle") {
        return false;
      }
    },
  }
);

// ------------------------- POST -------------------------
export const regradeWeeklyCheckin = createAsyncThunk<
  { response: WeeklyCheckInRegradeData; threadId: string },
  string,
  {
    pendingMeta: { threadId: string };
  }
>("weeklyCheckInMonitor/regradeWeeklyCheckin", async (threadId: string) => {
  if (isUsingAIService()) {
    const { success, data } = await aiServiceFetch<{
      success: boolean;
      data?: WeeklyCheckInTableRowDataBase & {
        threadId: string;
        userAccountId: number;
        isBurnoutRisk: 0 | 1;
        isTurnoverRisk: 0 | 1;
      };
    }>(`/v1/regradeWeeklyCheckin/${encodeURIComponent(threadId)}`, {
      method: "POST",
    });
    if (!success || !data) {
      throw new Error(`Failed to regrade weekly check-in for ${threadId}`);
    }
    const { moodScore, progressScore, readinessScore, isBurnoutRisk, isTurnoverRisk } =
      data;
    return {
      response: {
        moodScore,
        progressScore,
        readinessScore,
        isBurnoutRisk,
        isTurnoverRisk,
      },
      threadId,
    };
  }
  const requestUrl = `${REACT_APP_API_URL}/talentInsights/regradeWeeklyCheckin/${encodeURIComponent(threadId)}`;
  const response = (await request(requestUrl, {
    method: "POST",
  })) as WeeklyCheckInTableRowDataBase;
  return { response, threadId };
});

export const sendWeeklyCheckInReminders = createAsyncThunk(
  "weeklyCheckInMonitor/sendWeeklyCheckInReminders",
  async (userAccountIds: number[], { dispatch }) => {
    dispatch(
      setWeeklyCheckInModuleStatus({
        userAccountIds,
        responseStatus: "loading",
      })
    );

    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sendWeeklyCheckInReminders`;
    const response = (await request(
      requestUrl,
      {
        method: "POST",
        body: JSON.stringify({ userAccountIds }),
      },
      true
    )) as { error?: string; status: number };

    if (response.error) {
      dispatch(
        setWeeklyCheckInModuleStatus({
          userAccountIds,
          responseStatus: "failed",
        })
      );
      throw new Error(response.error);
    }

    dispatch(
      setWeeklyCheckInModuleStatus({
        userAccountIds,
        responseStatus: "succeeded",
      })
    );
    return userAccountIds;
  }
);

export const sendSampleWeeklyCheckInEmail = createAsyncThunk(
  "weeklyCheckInMonitor/sendSampleWeeklyCheckInEmail",
  async (
    emailType: Exclude<
      CheckInEmailReportType,
      "employee-conversation-summary-report"
    >,
    { getState }
  ) => {
    const {
      global: { currentUserAccountId },
    } = getState() as RootState;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/sendSampleWeeklyCheckIn`;
    await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({ emailType, userAccountId: currentUserAccountId }),
    });
  }
);

// ------------------------- PUT -------------------------
export const updateWeeklyCheckInCompanySettings = createAsyncThunk(
  "weeklyCheckInMonitor/updateWeeklyCheckInCompanySettings",
  async (payload: Partial<WeeklyCheckInCompanySettings>, { dispatch }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/weeklyCheckInsCompanySettings`;

    // if payload includes visibilityMode reset the table data
    if (payload.visibilityMode) {
      dispatch(resetWeeklyCheckInTableData());
    }
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    return payload;
  }
);

export const updateCompanyWeeklyCheckInCustomList = createAsyncThunk(
  "adminConsole/updateCompanyWeeklyCheckInCustomList",
  async (payload: Partial<WeeklyCheckInCustomList>, { getState, dispatch }) => {
    const { managers, departments, teams } = payload;
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/entitiesWithWeeklyCheckInsEnabled`;
    const response = await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify({
        managers,
        departmentIds: departments,
        teamIds: teams,
      }),
    });
    const existingCustomList = (getState() as RootState).weeklyCheckInMonitor
      .companyWeeklyCheckInCustomList;

    dispatch(getUserNotificationsAndEmailSettings({ forceRefresh: true }));
    const teamsToUpdate = getEntitiesToUpdateForCustomList(
      existingCustomList,
      payload,
      "teams"
    );
    const departmentsToUpdate = getEntitiesToUpdateForCustomList(
      existingCustomList,
      payload,
      "departments"
    );

    dispatch(updateTeamsBasedOnWeeklyCheckInCustomList(teamsToUpdate));
    dispatch(
      updateDepartmentsBasedOnWeeklyCheckInCustomList(departmentsToUpdate)
    );
    return response;
  }
);

export const updateExecutiveWeeklyCheckInCompanyCustomList = createAsyncThunk(
  "adminConsole/updateExecutiveWeeklyCheckInCompanyCustomList",
  async (payload: { userAccountIds: number[] }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/usersWithExecutiveReportEnabled`;
    await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    return payload.userAccountIds;
  }
);

export const acknowledgeHrConcernAlert = createAsyncThunk(
  "weeklyCheckInMonitor/acknowledgeHrConcernAlert",
  async (alertId: number) => {
    await request(
      `${REACT_APP_API_URL}/talentInsights/hrConcernAlerts/${alertId}/acknowledge`,
      { method: "PUT" }
    );
    return alertId;
  }
);

export const getHarmDetectionAlertRecipients = createAsyncThunk(
  "weeklyCheckInMonitor/getHarmDetectionAlertRecipients",
  async () => {
    const response = (await request(
      `${REACT_APP_API_URL}/talentInsights/hrConcernAlerts/recipients`
    )) as number[];
    return response ?? [];
  }
);

export const updateHarmDetectionAlertRecipients = createAsyncThunk(
  "weeklyCheckInMonitor/updateHarmDetectionAlertRecipients",
  async (payload: { userAccountIds: number[] }) => {
    await request(
      `${REACT_APP_API_URL}/talentInsights/hrConcernAlerts/recipients`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      }
    );
    return payload.userAccountIds;
  }
);

export const updateExternalExecutiveReportRecipients = createAsyncThunk(
  "weeklyCheckInMonitor/updateExternalExecutiveReportRecipients",
  async (payload: {
    externalRecipients: Array<{ emailAddress: string; recipientName?: string }>;
  }) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/externalExecutiveReportRecipients`;
    const response = (await request(requestUrl, {
      method: "PUT",
      body: JSON.stringify(payload),
    })) as { recipients: ExternalReportRecipient[] };
    return response?.recipients ?? [];
  }
);

export const updateCheckInCadence = createAsyncThunk(
  "weeklyCheckInMonitor/updateCheckInCadence",
  async (
    payload: WeeklyCheckInCadencePayload,
    { rejectWithValue }
  ) => {
    try {
      const response = await aiServiceFetch<{
        success: true;
        data: {
          previousFrequency: string;
          newFrequency: WeeklyCheckInCadence;
          appliedAtUtc: string;
        };
      }>("/v1/weeklyCheckInCadence", {
        method: "PUT",
        body: { checkInFrequency: payload.checkInFrequency },
      });
      return response.data.newFrequency;
    } catch (err) {
      // 409 WINDOW_OPEN: the current check-in window is still open.
      // Preserve windowClosesUtc so the modal can show the real date.
      if (err instanceof AiServiceError && err.status === 409) {
        const body = err.data as { windowClosesUtc?: string } | undefined;
        return rejectWithValue({ windowClosesUtc: body?.windowClosesUtc ?? null });
      }
      throw err;
    }
  }
);

// Read-only companion to the PUT above (DBC-704) — reports the current
// cycle's window state and, when previewFrequency is passed, whether
// switching to it right now would be blocked and the real date it would
// take effect.
export const getWeeklyCheckInCadenceSchedule = createAsyncThunk(
  "weeklyCheckInMonitor/getWeeklyCheckInCadenceSchedule",
  async (previewFrequency?: WeeklyCheckInCadence) => {
    const queryParams = getQueryParamsFromObject({
      ...(previewFrequency ? { previewFrequency } : {}),
    });
    const response = await aiServiceFetch<{
      success: true;
      data: WeeklyCheckInCadenceSchedule;
    }>(`/v1/weeklyCheckInCadence/schedule${queryParams ? `?${queryParams}` : ""}`, {
      method: "GET",
    });
    return response.data;
  }
);

// ------------------ Beginning of Slice Definition ------------------
export const weeklyCheckInMonitorSlice = createSlice({
  name: "weeklyCheckInMonitor",
  initialState,
  reducers: {
    setWeeklyCheckInModuleStatus: (
      state,
      {
        payload: { userAccountIds, responseStatus },
      }: PayloadAction<{
        userAccountIds: number[];
        responseStatus: responseStatus;
      }>
    ) => {
      const currentStatusMap =
        state.sendWeeklyCheckInRemindersPerUserAccountIdStatus;
      userAccountIds.forEach((userAccountId) => {
        currentStatusMap[userAccountId] = responseStatus;
      });
      state.sendWeeklyCheckInRemindersPerUserAccountIdStatus = currentStatusMap;
      const totalRemindersSent = userAccountIds.length;
      if (responseStatus === "succeeded") {
        toast.success(
          `${WEEKLY_CHECK_IN_FEATURE_NAME} reminder${getS(
            totalRemindersSent
          )} sent to ${totalRemindersSent} ${getPeopleOrPerson(
            totalRemindersSent
          )}`,
          successNotificationOptions
        );
      }
    },
    clearWeeklyCheckInCustomList: (state) => {
      return {
        ...state,
        companyWeeklyCheckInCustomList: {
          managers: [],
          departments: [],
          teams: [],
        },
      };
    },
    resetState: () => initialState,
    setGetWeeklyCheckInActivityEmailReportByIdStatus: (
      state,
      action: PayloadAction<{
        checkInEmailReportId: number;
        status: responseStatus;
      }>
    ) => {
      state.getWeeklyCheckInActivityEmailReportByIdStatus[
        action.payload.checkInEmailReportId
      ] = action.payload.status;
    },
    setShowWeeklyCheckInSampleData: (state, action: PayloadAction<boolean>) => {
      state.showWeeklyCheckInSampleData = action.payload;
    },
    setSampleWeeklyCheckInEmailReportModalState: (
      state,
      action: PayloadAction<SampleWeeklyCheckInEmailReportModalState>
    ) => {
      return {
        ...state,
        sampleWeeklyCheckInEmailReportModalState: {
          ...state.sampleWeeklyCheckInEmailReportModalState,
          ...action.payload,
        },
      };
    },
    resetSendSampleWeeklyCheckInEmail: (state) => {
      state.sendSampleWeeklyCheckInEmailStatus = "idle";
    },
    saveWeeklyCheckInCelebrateAndSupport: (
      state,
      action: PayloadAction<{
        response: CelebrateAndSupports;
        memoizedKey: string;
      }>
    ) => {
      state.celebrateAndSupports[action.payload.memoizedKey] =
        action.payload.response;
    },
    resetWeeklyCheckInTableData: (state) => {
      state.weeklyCheckInBreakdownTableData = {};
      state.allWeeklyCheckInTableData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCoachBoWeeklyCheckInActivityReport.pending, (state) => {
        state.getCoachBoWeeklyCheckInActivityReportStatus = "loading";
      })
      .addCase(
        getCoachBoWeeklyCheckInActivityReport.fulfilled,
        (state, action: PayloadAction<CoachBoWeeklyCheckInActivityReports>) => {
          state.getCoachBoWeeklyCheckInActivityReportStatus = "succeeded";
          state.coachBoWeeklyCheckInActivityReports = action.payload;
        }
      )
      .addCase(getCoachBoWeeklyCheckInActivityReport.rejected, (state) => {
        state.getCoachBoWeeklyCheckInActivityReportStatus = "failed";
      })
      .addCase(getWeeklyCheckInModuleStatus.pending, (state) => {
        state.getWeeklyCheckInModuleStatus = "loading";
      })
      .addCase(getWeeklyCheckInModuleStatus.fulfilled, (state, { payload }) => {
        state.getWeeklyCheckInModuleStatus = "succeeded";
        state.showWeeklyCheckInActivityEmptyState = !payload;
      })
      .addCase(getWeeklyCheckInModuleStatus.rejected, (state) => {
        state.getWeeklyCheckInModuleStatus = "failed";
      })
      .addCase(getWeeklyCheckInTableData.pending, (state, { meta }) => {
        state.getWeeklyCheckInTableDataStatus = "loading";
        if (startActivityRequest(state.weeklyCheckInTableDataOwner, meta)) {
          state.allWeeklyCheckInTableData = {};
        }
      })
      .addCase(
        getWeeklyCheckInTableData.fulfilled,
        (
          state,
          {
            payload: { response, entityType, timeInterval, companyKey },
          }: PayloadAction<{
            response: WeeklyCheckInTableRowData[];
            timeInterval: string;
            entityType: string;
            companyKey: string;
          }>
        ) => {
          if (companyKey !== state.weeklyCheckInTableDataOwner.companyKey) return;
          return {
            ...state,
            getWeeklyCheckInTableDataStatus: "succeeded",
            allWeeklyCheckInTableData: {
              ...state.allWeeklyCheckInTableData,
              [timeInterval]: {
                ...state.allWeeklyCheckInTableData[timeInterval],
                [entityType]: response,
              },
            },
          };
        }
      )
      .addCase(getWeeklyCheckInTableData.rejected, (state, { meta }) => {
        if (meta.requestId !== state.weeklyCheckInTableDataOwner.requestId) return;
        state.getWeeklyCheckInTableDataStatus = "failed";
      })
      // Adding cases for getWeeklyCheckInLineChartData
      .addCase(getWeeklyCheckInLineChartData.pending, (state) => {
        state.getWeeklyCheckInLineChartDataStatus = "loading";
      })
      .addCase(
        getWeeklyCheckInLineChartData.fulfilled,
        (
          state,
          action: PayloadAction<{
            response: WeeklyCheckInLineChartResponse | null;
            timeInterval: string;
            index: number;
          }>
        ) => {
          state.getWeeklyCheckInLineChartDataStatus = "succeeded";
          const { response, timeInterval, index } = action.payload;

          // Initialize the object structure if it doesn't exist
          if (!state.weeklyCheckInLineChartData[timeInterval]) {
            state.weeklyCheckInLineChartData[timeInterval] = {};
          }

          state.weeklyCheckInLineChartData[timeInterval][index] = response;
        }
      )
      .addCase(getWeeklyCheckInLineChartData.rejected, (state) => {
        state.getWeeklyCheckInLineChartDataStatus = "failed";
      })
      .addCase(getCheckInResponseSummary.pending, (state) => {
        state.getCheckInResponseSummaryStatus = "loading";
      })
      .addCase(
        getCheckInResponseSummary.fulfilled,
        (
          state,
          {
            payload: { response, threadId },
          }: PayloadAction<{
            response: CheckInResponseSummaryResponse;
            threadId: string;
          }>
        ) => {
          return {
            ...state,
            getCheckInResponseSummaryStatus: "succeeded",
            savedCheckInResponseSummaries: {
              ...state.savedCheckInResponseSummaries,
              [threadId]: response,
            },
          };
        }
      )
      .addCase(getCheckInResponseSummary.rejected, (state) => {
        state.getCheckInResponseSummaryStatus = "failed";
      })
      .addCase(regradeWeeklyCheckin.pending, (state, action) => {
        const threadId = action.meta.arg;
        state.regradeWeeklyCheckInStatusByThread = {
          ...state.regradeWeeklyCheckInStatusByThread,
          [threadId]: "loading",
        };
      })
      .addCase(
        regradeWeeklyCheckin.fulfilled,
        (
          state,
          {
            payload: { response, threadId },
          }: PayloadAction<{
            response: WeeklyCheckInRegradeData;
            threadId: string;
          }>
        ) => {
          toast.success(
            `Regrading ${WEEKLY_CHECK_IN_FEATURE_NAME.toLowerCase()} was successful!`,
            {
              ...successNotificationOptions,
            }
          );

          const allWeeklyCheckInTableData = updateRegradedCheckInInAllIntervals(
            state.allWeeklyCheckInTableData,
            response,
            threadId
          );

          const SubordinatesWeeklyCheckinTableData =
            updateRegradedCheckInInAllIntervals(
              state.SubordinatesWeeklyCheckinTableData,
              response,
              threadId
            );

          return {
            ...state,
            allWeeklyCheckInTableData,
            SubordinatesWeeklyCheckinTableData,
            regradeWeeklyCheckInStatusByThread: {
              ...state.regradeWeeklyCheckInStatusByThread,
              [threadId]: "succeeded",
            },
          };
        }
      )
      .addCase(regradeWeeklyCheckin.rejected, (state, action) => {
        const threadId = action.meta.arg;
        return {
          ...state,
          regradeWeeklyCheckInStatusByThread: {
            ...state.regradeWeeklyCheckInStatusByThread,
            [threadId]: "failed",
          },
        };
      })
      .addCase(getSubordinatesWeeklyCheckinTableData.pending, (state) => {
        state.getSubordinatesWeeklyCheckinTableDataStatus = "loading";
      })
      .addCase(
        getSubordinatesWeeklyCheckinTableData.fulfilled,
        (
          state,
          {
            payload: { response, entityType, timeInterval },
          }: PayloadAction<{
            response: WeeklyCheckInTableRowData[];
            timeInterval: string;
            entityType: string;
          }>
        ) => {
          return {
            ...state,
            getSubordinatesWeeklyCheckinTableDataStatus: "succeeded",
            SubordinatesWeeklyCheckinTableData: {
              ...state.SubordinatesWeeklyCheckinTableData,
              [timeInterval]: {
                ...state.SubordinatesWeeklyCheckinTableData[timeInterval],
                [entityType]: response,
              },
            },
          };
        }
      )
      .addCase(getSubordinatesWeeklyCheckinTableData.rejected, (state) => {
        state.getSubordinatesWeeklyCheckinTableDataStatus = "failed";
      })
      .addCase(getSubordinatesWeeklyCheckInLineChartData.pending, (state) => {
        state.getSubordinatesWeeklyCheckInLineChartDataStatus = "loading";
      })
      .addCase(
        getSubordinatesWeeklyCheckInLineChartData.fulfilled,
        (
          state,
          action: PayloadAction<{
            response: WeeklyCheckInLineChartResponse | null;
            timeInterval: string;
            index: number;
          }>
        ) => {
          state.getSubordinatesWeeklyCheckInLineChartDataStatus = "succeeded";
          const { response, timeInterval, index } = action.payload;

          // Initialize the object structure if it doesn't exist
          if (!state.SubordinatesWeeklyCheckInLineChartData[timeInterval]) {
            state.SubordinatesWeeklyCheckInLineChartData[timeInterval] = {};
          }

          state.SubordinatesWeeklyCheckInLineChartData[timeInterval][index] =
            response;
        }
      )
      .addCase(getSubordinatesWeeklyCheckInLineChartData.rejected, (state) => {
        state.getSubordinatesWeeklyCheckInLineChartDataStatus = "failed";
      })
      .addCase(getSubordinatesWeeklyCheckInActivityReport.pending, (state) => {
        state.getSubordinatesWeeklyCheckInActivityReportStatus = "loading";
      })
      .addCase(
        getSubordinatesWeeklyCheckInActivityReport.fulfilled,
        (state, action: PayloadAction<CoachBoWeeklyCheckInActivityReports>) => {
          state.getSubordinatesWeeklyCheckInActivityReportStatus = "succeeded";
          state.subordinatesWeeklyCheckInActivityReports = action.payload;
        }
      )
      .addCase(getSubordinatesWeeklyCheckInActivityReport.rejected, (state) => {
        state.getSubordinatesWeeklyCheckInActivityReportStatus = "failed";
      })
      .addCase(getUserSpanOfControl.pending, (state) => {
        state.getSpanOfControlStatus = "loading";
      })
      .addCase(
        getUserSpanOfControl.fulfilled,
        (state, { payload }: PayloadAction<number[]>) => {
          state.getSpanOfControlStatus = "succeeded";
          state.usersSubordinatesIds = payload;
        }
      )
      .addCase(getUserSpanOfControl.rejected, (state) => {
        state.getSpanOfControlStatus = "failed";
      })
      .addCase(getWeeklyCheckInUserStatuses.pending, (state) => {
        state.getWeeklyCheckInUserStatusesStatus = "loading";
      })
      .addCase(
        getWeeklyCheckInUserStatuses.fulfilled,
        (state, { payload }: PayloadAction<WeeklyCheckInUserStatuses>) => {
          state.getWeeklyCheckInUserStatusesStatus = "succeeded";
          state.weeklyCheckInUserStatuses = payload;
        }
      )
      .addCase(getWeeklyCheckInUserStatuses.rejected, (state) => {
        state.getWeeklyCheckInUserStatusesStatus = "failed";
      })
      .addCase(sendWeeklyCheckInReminders.pending, (state) => {
        state.sendWeeklyCheckInRemindersStatus = "loading";
      })
      .addCase(
        sendWeeklyCheckInReminders.fulfilled,
        (state, action: PayloadAction<number[]>) => {
          let newUsersWhoHaveBeenRemindedInTheLastFiveMinutes: number[] = [];
          const pendingUsers =
            state.weeklyCheckInUserStatuses?.pendingUsers ?? [];
          if (state.weeklyCheckInUserStatuses) {
            newUsersWhoHaveBeenRemindedInTheLastFiveMinutes =
              state.weeklyCheckInUserStatuses.usersWhoHaveBeenRemindedInTheLastFiveMinutes.concat(
                action.payload
              );

            newUsersWhoHaveBeenRemindedInTheLastFiveMinutes = [
              ...new Set(newUsersWhoHaveBeenRemindedInTheLastFiveMinutes),
            ];
          }

          return {
            ...state,
            sendWeeklyCheckInRemindersStatus: "succeeded",
            weeklyCheckInUserStatuses: {
              ...state.weeklyCheckInUserStatuses,
              usersWhoHaveBeenRemindedInTheLastFiveMinutes:
                newUsersWhoHaveBeenRemindedInTheLastFiveMinutes,
              pendingUsers,
            },
          };
        }
      )
      .addCase(
        getWeeklyCheckInActivityEmailReportsTableData.pending,
        (state, { meta }) => {
          state.getWeeklyCheckInActivityEmailReportsTableDataStatus = "loading";
          if (
            startActivityRequest(
              state.weeklyCheckInActivityEmailReportsTableDataOwner,
              meta
            )
          ) {
            state.weeklyCheckInActivityEmailReportsTableData = {};
          }
        }
      )
      .addCase(
        getWeeklyCheckInActivityEmailReportsTableData.fulfilled,
        (
          state,
          {
            payload: { response, reportType, timeInterval, companyKey },
          }: PayloadAction<{
            response: WeeklyCheckInActivityEmailReportTableData[];
            timeInterval: string;
            reportType: string;
            companyKey: string;
          }>
        ) => {
          if (
            companyKey !==
            state.weeklyCheckInActivityEmailReportsTableDataOwner.companyKey
          ) {
            return;
          }
          return {
            ...state,
            getWeeklyCheckInActivityEmailReportsTableDataStatus: "succeeded",
            weeklyCheckInActivityEmailReportsTableData: {
              ...state.weeklyCheckInActivityEmailReportsTableData,
              [timeInterval]: {
                ...state.weeklyCheckInActivityEmailReportsTableData[
                  timeInterval
                ],
                [reportType]: response,
              },
            },
          };
        }
      )
      .addCase(sendWeeklyCheckInReminders.rejected, (state) => {
        state.sendWeeklyCheckInRemindersStatus = "failed";
      })
      .addCase(getCompanyWeeklyCheckInCustomList.pending, (state) => {
        state.getCompanyWeeklyCheckInCustomListStatus = "loading";
      })
      .addCase(getCompanyWeeklyCheckInCustomList.rejected, (state) => {
        state.getCompanyWeeklyCheckInCustomListStatus = "failed";
      })
      .addCase(
        getCompanyWeeklyCheckInCustomList.fulfilled,
        (state, { payload }) => {
          state.getCompanyWeeklyCheckInCustomListStatus = "succeeded";
          state.companyWeeklyCheckInCustomList = payload;
        }
      )
      .addCase(updateCompanyWeeklyCheckInCustomList.pending, (state) => {
        state.updateCompanyWeeklyCheckInCustomListStatus = "loading";
      })
      .addCase(updateCompanyWeeklyCheckInCustomList.rejected, (state) => {
        state.updateCompanyWeeklyCheckInCustomListStatus = "failed";
      })
      .addCase(
        updateCompanyWeeklyCheckInCustomList.fulfilled,
        (state, { payload }) => {
          toast.success(
            `${WEEKLY_CHECK_IN_FEATURE_NAME} Custom List Updated Successfully`,
            successNotificationOptions
          );
          if (!state.companyWeeklyCheckInCustomList) {
            return {
              ...state,
              updateCompanyWeeklyCheckInCustomListStatus: "succeeded",
            };
          }
          return {
            ...state,
            updateCompanyWeeklyCheckInCustomListStatus: "succeeded",
            companyWeeklyCheckInCustomList: {
              ...state.companyWeeklyCheckInCustomList,
              ...payload,
            },
          };
        }
      )
      .addCase(getWeeklyCheckInCompanySettings.pending, (state) => {
        state.getWeeklyCheckInCompanySettingsStatus = "loading";
      })
      .addCase(getWeeklyCheckInCompanySettings.rejected, (state) => {
        state.getWeeklyCheckInCompanySettingsStatus = "failed";
      })
      .addCase(
        getWeeklyCheckInCompanySettings.fulfilled,
        (state, { payload }) => {
          state.getWeeklyCheckInCompanySettingsStatus = "succeeded";
          state.weeklyCheckInCompanySettings = payload;
        }
      )
      .addCase(updateWeeklyCheckInCompanySettings.pending, (state) => {
        state.updateWeeklyCheckInCompanySettingsStatus = "loading";
      })
      .addCase(updateWeeklyCheckInCompanySettings.rejected, (state) => {
        state.updateWeeklyCheckInCompanySettingsStatus = "failed";
      })
      .addCase(
        updateWeeklyCheckInCompanySettings.fulfilled,
        (state, { payload }) => {
          const toastMessage =
            getUpdateWeeklyCheckInCompanySettingsToastMessage(payload);
          if (toastMessage) {
            toast.success(toastMessage, successNotificationOptions);
          }

          if (!state.weeklyCheckInCompanySettings) {
            return {
              ...state,
              updateWeeklyCheckInCompanySettingsStatus: "succeeded",
            };
          }
          return {
            ...state,
            updateWeeklyCheckInCompanySettingsStatus: "succeeded",
            weeklyCheckInCompanySettings: {
              ...state.weeklyCheckInCompanySettings,
              ...payload,
            },
          };
        }
      )
      .addCase(updateCheckInCadence.fulfilled, (state, { payload }) => {
        return {
          ...state,
          weeklyCheckInCompanySettings: state.weeklyCheckInCompanySettings
            ? {
                ...state.weeklyCheckInCompanySettings,
                checkInFrequency: payload,
              }
            : null,
        };
      })
      .addCase(getWeeklyCheckInCadenceSchedule.pending, (state) => {
        state.getWeeklyCheckInCadenceScheduleStatus = "loading";
      })
      .addCase(
        getWeeklyCheckInCadenceSchedule.fulfilled,
        (state, { payload }) => {
          state.getWeeklyCheckInCadenceScheduleStatus = "succeeded";
          state.weeklyCheckInCadenceSchedule = payload;
        }
      )
      .addCase(getWeeklyCheckInCadenceSchedule.rejected, (state) => {
        state.getWeeklyCheckInCadenceScheduleStatus = "failed";
      })
      .addCase(
        getWeeklyCheckInActivityEmailReportsTableData.rejected,
        (state, { meta }) => {
          if (
            meta.requestId !==
            state.weeklyCheckInActivityEmailReportsTableDataOwner.requestId
          ) {
            return;
          }
          state.getWeeklyCheckInActivityEmailReportsTableDataStatus = "failed";
        }
      )
      .addCase(
        getWeeklyCheckInActivityEmailReportById.fulfilled,
        (
          state,
          {
            payload: { response, checkInEmailReportId },
          }: PayloadAction<{
            response: WeeklyCheckInActivityEmailReport;
            checkInEmailReportId: number;
          }>
        ) => {
          return {
            ...state,
            weeklyCheckInActivityEmailReports: {
              ...state.weeklyCheckInActivityEmailReports,
              [checkInEmailReportId]: response,
            },
          };
        }
      )
      .addCase(
        updateExecutiveWeeklyCheckInCompanyCustomList.pending,
        (state) => {
          state.updatingCompanyExecutiveWeeklyCheckInCustomListStatus =
            "loading";
        }
      )
      .addCase(
        updateExecutiveWeeklyCheckInCompanyCustomList.rejected,
        (state) => {
          state.updatingCompanyExecutiveWeeklyCheckInCustomListStatus =
            "failed";
        }
      )
      .addCase(
        getSubordinatesWeeklyCheckInActivityEmailReportsTableData.fulfilled,
        (
          state,
          {
            payload: { response, timeInterval, reportType },
          }: PayloadAction<{
            response: WeeklyCheckInActivityEmailReportTableData[];
            timeInterval: string;
            reportType: string;
          }>
        ) => {
          return {
            ...state,
            getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus:
              "succeeded",
            subordinatesWeeklyCheckInActivityEmailReportsTableData: {
              ...state.subordinatesWeeklyCheckInActivityEmailReportsTableData,
              [timeInterval]: {
                ...state.subordinatesWeeklyCheckInActivityEmailReportsTableData[
                  timeInterval
                ],
                [reportType]: response,
              },
            },
          };
        }
      )
      .addCase(
        getSubordinatesWeeklyCheckInActivityEmailReportsTableData.rejected,
        (state) => {
          state.getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus =
            "failed";
        }
      )
      .addCase(
        updateExecutiveWeeklyCheckInCompanyCustomList.fulfilled,
        (state, { payload }) => {
          state.updatingCompanyExecutiveWeeklyCheckInCustomListStatus =
            "succeeded";
          toast.success(
            `Executive ${WEEKLY_CHECK_IN_FEATURE_NAME} Report Custom List Updated Successfully`,
            successNotificationOptions
          );
          state.executiveWeeklyCheckInCompanyCustomList = payload;
        }
      )
      .addCase(getExecutiveWeeklyCheckInCompanyCustomList.pending, (state) => {
        state.getExecutiveWeeklyCheckInCompanyCustomListStatus = "loading";
      })
      .addCase(
        getExecutiveWeeklyCheckInCompanyCustomList.fulfilled,
        (state, { payload }) => {
          state.getExecutiveWeeklyCheckInCompanyCustomListStatus = "succeeded";
          state.executiveWeeklyCheckInCompanyCustomList = payload;
        }
      )
      .addCase(getExecutiveWeeklyCheckInCompanyCustomList.rejected, (state) => {
        state.getExecutiveWeeklyCheckInCompanyCustomListStatus = "failed";
      })
      .addCase(getExternalExecutiveReportRecipients.pending, (state) => {
        state.getExternalExecutiveReportRecipientsStatus = "loading";
      })
      .addCase(
        getExternalExecutiveReportRecipients.fulfilled,
        (state, { payload }) => {
          state.getExternalExecutiveReportRecipientsStatus = "succeeded";
          state.externalExecutiveReportRecipients = payload;
        }
      )
      .addCase(getExternalExecutiveReportRecipients.rejected, (state) => {
        state.getExternalExecutiveReportRecipientsStatus = "failed";
      })
      .addCase(updateExternalExecutiveReportRecipients.pending, (state) => {
        state.updatingExternalExecutiveReportRecipientsStatus = "loading";
      })
      .addCase(
        updateExternalExecutiveReportRecipients.fulfilled,
        (state, { payload }) => {
          state.updatingExternalExecutiveReportRecipientsStatus = "succeeded";
          toast.success(
            `External ${WEEKLY_CHECK_IN_FEATURE_NAME} Report Recipients Updated Successfully`,
            successNotificationOptions
          );
          state.externalExecutiveReportRecipients = payload;
        }
      )
      .addCase(updateExternalExecutiveReportRecipients.rejected, (state) => {
        state.updatingExternalExecutiveReportRecipientsStatus = "failed";
      })
      .addCase(
        getSubordinatesWeeklyCheckInActivityEmailReportsTableData.pending,
        (state) => {
          state.getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus =
            "loading";
        }
      )
      .addCase(getSampleWeeklyCheckInActivityData.pending, (state) => {
        state.getSampleWeeklyCheckInActivityDataStatus = "loading";
      })
      .addCase(
        getSampleWeeklyCheckInActivityData.fulfilled,
        (state, action) => {
          state.getSampleWeeklyCheckInActivityDataStatus = "succeeded";
          state.sampleWeeklyCheckInActivityData = action.payload;
        }
      )
      .addCase(getSampleWeeklyCheckInActivityData.rejected, (state) => {
        state.getSampleWeeklyCheckInActivityDataStatus = "failed";
      })
      .addCase(sendSampleWeeklyCheckInEmail.pending, (state) => {
        state.sendSampleWeeklyCheckInEmailStatus = "loading";
      })
      .addCase(sendSampleWeeklyCheckInEmail.fulfilled, (state) => {
        toast.success(
          `Sample Email Sent Successfully`,
          successNotificationOptions
        );
        state.sendSampleWeeklyCheckInEmailStatus = "succeeded";
      })
      .addCase(sendSampleWeeklyCheckInEmail.rejected, (state) => {
        state.sendSampleWeeklyCheckInEmailStatus = "failed";
      })
      .addCase(getWeeklyCheckInCelebrateAndSupport.pending, (state) => {
        state.getWeeklyCheckInCelebrateAndSupportStatus = "loading";
      })
      .addCase(getWeeklyCheckInCelebrateAndSupport.fulfilled, (state) => {
        state.getWeeklyCheckInCelebrateAndSupportStatus = "succeeded";
      })
      .addCase(getWeeklyCheckInCelebrateAndSupport.rejected, (state) => {
        state.getWeeklyCheckInCelebrateAndSupportStatus = "failed";
      })
      .addCase(
        getWeeklyCheckInCelebrateAndSupportForSubordinates.pending,
        (state) => {
          state.getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus =
            "loading";
        }
      )
      .addCase(
        getWeeklyCheckInCelebrateAndSupportForSubordinates.fulfilled,
        (state) => {
          state.getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus =
            "succeeded";
        }
      )
      .addCase(
        getWeeklyCheckInCelebrateAndSupportForSubordinates.rejected,
        (state) => {
          state.getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus =
            "failed";
        }
      )
      .addCase(getUserCheckInsFromEntityAndTimeInterval.pending, (state) => {
        state.getWeeklyCheckInBreakdownTableDataStatus = "loading";
      })
      .addCase(
        getUserCheckInsFromEntityAndTimeInterval.fulfilled,
        (state, action) => {
          return {
            ...state,
            getWeeklyCheckInBreakdownTableDataStatus: "succeeded",
            weeklyCheckInBreakdownTableData: {
              ...state.weeklyCheckInBreakdownTableData,
              [action.payload.payloadKey]: action.payload.response,
            },
          };
        }
      )
      .addCase(getUserCheckInsFromEntityAndTimeInterval.rejected, (state) => {
        state.getWeeklyCheckInBreakdownTableDataStatus = "failed";
      })
      .addCase(getIndividualCheckInActivitySnapshot.pending, (state) => {
        state.getIndividualCheckInActivitySnapshotStatus = "loading";
      })
      .addCase(
        getIndividualCheckInActivitySnapshot.fulfilled,
        (state, action) => {
          state.getIndividualCheckInActivitySnapshotStatus = "succeeded";
          state.individualCheckInActivitySnapshot = action.payload;
        }
      )
      .addCase(getIndividualCheckInActivitySnapshot.rejected, (state) => {
        state.getIndividualCheckInActivitySnapshotStatus = "failed";
      })
      .addCase(acknowledgeHrConcernAlert.pending, (state) => {
        state.acknowledgeHrConcernAlertStatus = "loading";
      })
      .addCase(
        acknowledgeHrConcernAlert.fulfilled,
        (state, { payload: alertId }: PayloadAction<number>) => {
          state.acknowledgeHrConcernAlertStatus = "succeeded";

          // Clear the concern from every time-interval bucket in both table stores
          clearAlertFromStore(state.allWeeklyCheckInTableData, alertId);
          clearAlertFromStore(
            state.SubordinatesWeeklyCheckinTableData,
            alertId
          );
        }
      )
      .addCase(acknowledgeHrConcernAlert.rejected, (state) => {
        state.acknowledgeHrConcernAlertStatus = "failed";
      })
      .addCase(getHarmDetectionAlertRecipients.pending, (state) => {
        state.getHarmDetectionAlertRecipientsStatus = "loading";
      })
      .addCase(getHarmDetectionAlertRecipients.fulfilled, (state, action) => {
        state.getHarmDetectionAlertRecipientsStatus = "succeeded";
        state.harmDetectionAlertRecipients = action.payload;
      })
      .addCase(getHarmDetectionAlertRecipients.rejected, (state) => {
        state.getHarmDetectionAlertRecipientsStatus = "failed";
      })
      .addCase(updateHarmDetectionAlertRecipients.pending, (state) => {
        state.updateHarmDetectionAlertRecipientsStatus = "loading";
      })
      .addCase(
        updateHarmDetectionAlertRecipients.fulfilled,
        (state, action) => {
          state.updateHarmDetectionAlertRecipientsStatus = "succeeded";
          state.harmDetectionAlertRecipients = action.payload;
        }
      )
      .addCase(updateHarmDetectionAlertRecipients.rejected, (state) => {
        state.updateHarmDetectionAlertRecipientsStatus = "failed";
      });
  },
});

// ------------------ Actions ------------------
export const {
  resetState,
  setGetWeeklyCheckInActivityEmailReportByIdStatus,
  setWeeklyCheckInModuleStatus,
  clearWeeklyCheckInCustomList,
  setShowWeeklyCheckInSampleData,
  setSampleWeeklyCheckInEmailReportModalState,
  resetSendSampleWeeklyCheckInEmail,
  saveWeeklyCheckInCelebrateAndSupport,
  resetWeeklyCheckInTableData,
} = weeklyCheckInMonitorSlice.actions;

// ------------------ Selectors ------------------
export const selectGetCoachBoWeeklyCheckInActivityReportStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getCoachBoWeeklyCheckInActivityReportStatus;
export const selectCoachBoWeeklyCheckInActivityReports = (state: RootState) =>
  state.weeklyCheckInMonitor.coachBoWeeklyCheckInActivityReports;
export const selectShowWeeklyCheckInActivityEmptyState = (state: RootState) =>
  state.weeklyCheckInMonitor.showWeeklyCheckInActivityEmptyState;
export const selectGetWeeklyCheckInModuleStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getWeeklyCheckInModuleStatus;
export const selectGetWeeklyCheckInTableDataStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getWeeklyCheckInTableDataStatus;
export const selectWeeklyCheckInLineChartData = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInLineChartData;
export const selectGetWeeklyCheckInLineChartDataStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getWeeklyCheckInLineChartDataStatus;
export const selectCheckInResponseSummary = (
  state: RootState,
  threadId: string
) => state.weeklyCheckInMonitor.savedCheckInResponseSummaries[threadId];
export const selectCheckInResponseSummaries = (state: RootState) =>
  state.weeklyCheckInMonitor.savedCheckInResponseSummaries;
export const selectGetCheckInResponseSummaryStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getCheckInResponseSummaryStatus;
export const selectRegradeWeeklyCheckInStatusByThread = (state: RootState) =>
  state.weeklyCheckInMonitor.regradeWeeklyCheckInStatusByThread;
export const selectAllWeeklyCheckInTableData = (state: RootState) =>
  state.weeklyCheckInMonitor.allWeeklyCheckInTableData;
export const selectSubordinatesWeeklyCheckinTableData = (state: RootState) =>
  state.weeklyCheckInMonitor.SubordinatesWeeklyCheckinTableData;
export const selectSubordinatesWeeklyCheckInLineChartData = (
  state: RootState
) => state.weeklyCheckInMonitor.SubordinatesWeeklyCheckInLineChartData;
export const selectSubordinatesWeeklyCheckInActivityReports = (
  state: RootState
) => state.weeklyCheckInMonitor.subordinatesWeeklyCheckInActivityReports;
export const selectGetSubordinatesWeeklyCheckinTableDataStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getSubordinatesWeeklyCheckinTableDataStatus;
export const selectGetSubordinatesWeeklyCheckInLineChartDataStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getSubordinatesWeeklyCheckInLineChartDataStatus;
export const selectGetSubordinatesWeeklyCheckInActivityReportStatus = (
  state: RootState
) =>
  state.weeklyCheckInMonitor.getSubordinatesWeeklyCheckInActivityReportStatus;
export const selectGetSpanOfControlStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getSpanOfControlStatus;
export const selectUsersSubordinatesIds = (state: RootState) =>
  state.weeklyCheckInMonitor.usersSubordinatesIds;
export const selectGetWeeklyCheckInUserStatusesStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getWeeklyCheckInUserStatusesStatus;
export const selectWeeklyCheckInUserStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInUserStatuses;
export const selectSendWeeklyCheckInRemindersStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.sendWeeklyCheckInRemindersStatus;
export const selectSendWeeklyCheckInRemindersPerUserAccountIdStatus = (
  state: RootState
) =>
  state.weeklyCheckInMonitor.sendWeeklyCheckInRemindersPerUserAccountIdStatus;
export const selectWeeklyCheckInCustomList = (state: RootState) =>
  state.weeklyCheckInMonitor.companyWeeklyCheckInCustomList;
export const selectWeeklyCheckInCadenceSchedule = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInCadenceSchedule;
export const selectGetWeeklyCheckInCadenceScheduleStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getWeeklyCheckInCadenceScheduleStatus;
export const selectUpdateWeeklyCheckInCompanySettingsStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.updateWeeklyCheckInCompanySettingsStatus;
export const selectWeeklyCheckInCompanySettings = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInCompanySettings;
export const selectGetWeeklyCheckInCompanySettingsStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getWeeklyCheckInCompanySettingsStatus;
export const selectUpdateCompanyWeeklyCheckInCustomListStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.updateCompanyWeeklyCheckInCustomListStatus;
export const selectExecutiveWeeklyCheckInCompanyCustomList = (
  state: RootState
) => state.weeklyCheckInMonitor.executiveWeeklyCheckInCompanyCustomList;
export const selectUpdatingCompanyExecutiveWeeklyCheckInCustomListStatus = (
  state: RootState
) =>
  state.weeklyCheckInMonitor
    .updatingCompanyExecutiveWeeklyCheckInCustomListStatus;
export const selectExternalExecutiveReportRecipients = (state: RootState) =>
  state.weeklyCheckInMonitor.externalExecutiveReportRecipients;
export const selectGetExternalExecutiveReportRecipientsStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getExternalExecutiveReportRecipientsStatus;
export const selectUpdatingExternalExecutiveReportRecipientsStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.updatingExternalExecutiveReportRecipientsStatus;
export const selectWeeklyCheckInActivityEmailReportsTableData = (
  state: RootState
) => state.weeklyCheckInMonitor.weeklyCheckInActivityEmailReportsTableData;
export const selectWeeklyCheckInActivityEmailReports = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInActivityEmailReports;
export const selectGetWeeklyCheckInActivityEmailReportsTableDataStatus = (
  state: RootState
) =>
  state.weeklyCheckInMonitor
    .getWeeklyCheckInActivityEmailReportsTableDataStatus;
export const selectGetWeeklyCheckInActivityEmailReportByIdStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getWeeklyCheckInActivityEmailReportByIdStatus;
export const selectGetSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus =
  (state: RootState) =>
    state.weeklyCheckInMonitor
      .getSubordinatesWeeklyCheckInActivityEmailReportsTableDataStatus;
export const selectSubordinatesWeeklyCheckInActivityEmailReportsTableData = (
  state: RootState
) =>
  state.weeklyCheckInMonitor
    .subordinatesWeeklyCheckInActivityEmailReportsTableData;
export const selectGetCompanyWeeklyCheckInCustomListStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getCompanyWeeklyCheckInCustomListStatus;
export const selectGetSampleWeeklyCheckInActivityReportStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getSampleWeeklyCheckInActivityDataStatus;
export const selectSampleWeeklyCheckInActivityData = (state: RootState) =>
  state.weeklyCheckInMonitor.sampleWeeklyCheckInActivityData;
export const selectShowWeeklyCheckInSampleData = (state: RootState) =>
  state.weeklyCheckInMonitor.showWeeklyCheckInSampleData;
export const selectSampleWeeklyCheckInEmailReportModalState = (
  state: RootState
) => state.weeklyCheckInMonitor.sampleWeeklyCheckInEmailReportModalState;
export const selectSendSampleWeeklyCheckInEmailStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.sendSampleWeeklyCheckInEmailStatus;
export const selectGetWeeklyCheckInCelebrateAndSupportStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getWeeklyCheckInCelebrateAndSupportStatus;
export const selectGetWeeklyCheckInCelebrateAndSupportForSubordinatesStatus = (
  state: RootState
) =>
  state.weeklyCheckInMonitor
    .getWeeklyCheckInCelebrateAndSupportForSubordinatesStatus;
export const selectCelebrateAndSupports = (state: RootState) =>
  state.weeklyCheckInMonitor.celebrateAndSupports;
export const selectGetWeeklyCheckInBreakdownTableDataStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getWeeklyCheckInBreakdownTableDataStatus;
export const selectWeeklyCheckInBreakdownTableData = (state: RootState) =>
  state.weeklyCheckInMonitor.weeklyCheckInBreakdownTableData;
export const selectGetIndividualCheckInActivitySnapshotStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.getIndividualCheckInActivitySnapshotStatus;
export const selectIndividualCheckInActivitySnapshot = (state: RootState) =>
  state.weeklyCheckInMonitor.individualCheckInActivitySnapshot;
export const selectAcknowledgeHrConcernAlertStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.acknowledgeHrConcernAlertStatus;
export const selectHarmDetectionAlertRecipients = (state: RootState) =>
  state.weeklyCheckInMonitor.harmDetectionAlertRecipients;
export const selectGetHarmDetectionAlertRecipientsStatus = (state: RootState) =>
  state.weeklyCheckInMonitor.getHarmDetectionAlertRecipientsStatus;
export const selectUpdateHarmDetectionAlertRecipientsStatus = (
  state: RootState
) => state.weeklyCheckInMonitor.updateHarmDetectionAlertRecipientsStatus;

export default weeklyCheckInMonitorSlice.reducer;
